import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckSquare, Square } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formApi } from "@/lib/api";
import { siteSettings } from "@/content/site";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import WhatsAppFloat from "@/components/WhatsAppFloat";

const STEPS = [
  { key: "comune", title: "Dove si trova il bagno da ristrutturare?", custom: "comune" },
  { key: "unico_bagno", title: "Il bagno è l'unico della casa?", options: ["Sì", "No"] },
  { key: "eta_bagno", title: "Da quanto tempo non viene ristrutturato?", options: ["Meno di 10 anni", "10-20 anni", "Più di 20 anni", "Non lo so"] },
  { key: "tipo_intervento", title: "Che tipo di intervento stai valutando?", options: ["Ristrutturazione completa", "Rinnovo parziale", "Sostituzione vasca/doccia", "Non lo so, voglio un consiglio"] },
  { key: "urgenza", title: "Quanto è urgente?", options: ["Subito", "Entro 1 mese", "Entro 3 mesi", "Sto solo valutando"] },
  { key: "details", title: "Aggiungi qualche informazione utile sul bagno.", custom: "details" },
  { key: "contact", title: "Vuoi essere ricontattato per valutare il sopralluogo?", custom: "form" },
];

export default function PrenotaSopralluogo() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [zone, setZone] = useState({ comune: "", indirizzo: "" });
  const [details, setDetails] = useState({ tipo_casa: "", piano: "", ascensore: "", messaggio: "" });
  const [lead, setLead] = useState({ first_name: "", last_name: "", phone: "", email: "", privacy: false });
  const [submitting, setSubmitting] = useState(false);

  const total = STEPS.length;
  const progress = ((step + 1) / total) * 100;
  const current = STEPS[step];

  const next = () => setStep((s) => Math.min(s + 1, total - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const canNext =
    current.custom === "comune" ? !!zone.comune :
    current.custom === "details" ? true :
    current.custom === "form" ? true :
    !!answers[current.key];

  const selectAnswer = (key, opt) => {
    setAnswers({ ...answers, [key]: opt });
    setTimeout(next, 220);
  };

  const submit = async () => {
    if (!lead.first_name || !lead.last_name || !lead.phone || !lead.email || !lead.privacy) {
      toast.error("Qualcosa non ha funzionato. Riprova oppure contattaci direttamente su WhatsApp.");
      return;
    }

    setSubmitting(true);
    try {
      await formApi.createSopralluogoLead({
        first_name: lead.first_name,
        last_name: lead.last_name,
        phone: lead.phone,
        email: lead.email,
        comune: zone.comune,
        indirizzo_intervento: zone.indirizzo,
        unico_bagno: answers.unico_bagno || "",
        eta_bagno: answers.eta_bagno || "",
        tipo_intervento: answers.tipo_intervento || "",
        urgenza: answers.urgenza || "",
        tipo_casa: details.tipo_casa,
        piano: details.piano,
        ascensore: details.ascensore,
        messaggio: details.messaggio,
        privacy_consent: lead.privacy,
      });
      navigate("/grazie-sopralluogo");
    } catch {
      toast.error("Qualcosa non ha funzionato. Riprova oppure contattaci direttamente su WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-white">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Progress value={progress} className="h-1 rounded-none bg-sand-soft" />
      </div>

      <div className="max-w-2xl mx-auto px-5 md:px-8 pt-16 pb-32 min-h-screen flex flex-col justify-center">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="text-sm text-[#5C5C59] hover:text-oasi-green">← Oasi Bagni</Link>
          <div className="text-xs text-[#5C5C59]">Passo {step + 1} di {total}</div>
        </div>

        {step === 0 && (
          <div className="mb-6">
            <div className="overline mb-3">Richiedi il tuo sopralluogo gratuito</div>
            <p className="text-sm text-[#5C5C59] italic">Compila il modulo con alcune informazioni sul tuo bagno. Ti ricontatteremo per capire meglio il progetto e valutare il sopralluogo.</p>
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
            <div className="overline mb-4">Sopralluogo gratuito</div>
            <h1 className="font-display text-3xl md:text-4xl leading-tight mb-8" data-testid={`sop-title-${step}`}>{current.title}</h1>

            {current.options && (
              <div className="space-y-3">
                {current.options.map((opt) => (
                  <button key={opt} onClick={() => selectAnswer(current.key, opt)}
                    className={`w-full text-left rounded-2xl p-5 border transition-all flex items-start gap-3 ${answers[current.key] === opt ? "border-oasi-green bg-white shadow-sm" : "border-brand bg-white hover:border-oasi-green/50"}`}>
                    {answers[current.key] === opt ? <CheckSquare size={22} weight="fill" className="text-oasi-green mt-0.5" /> : <Square size={22} weight="light" className="text-[#5C5C59] mt-0.5" />}
                    <div className="font-medium">{opt}</div>
                  </button>
                ))}
              </div>
            )}

            {current.custom === "comune" && (
              <div className="space-y-3">
                <Input autoFocus placeholder="Comune *" value={zone.comune} onChange={(e) => setZone({ ...zone, comune: e.target.value })} className="h-12" data-testid="sop-comune" />
                <Input placeholder="Indirizzo intervento" value={zone.indirizzo} onChange={(e) => setZone({ ...zone, indirizzo: e.target.value })} className="h-12" />
              </div>
            )}

            {current.custom === "details" && (
              <div className="space-y-3">
                <Input placeholder="Il bagno è in: appartamento, villetta, casa indipendente..." value={details.tipo_casa} onChange={(e) => setDetails({ ...details, tipo_casa: e.target.value })} className="h-12" />
                <Input placeholder="A che piano si trova?" value={details.piano} onChange={(e) => setDetails({ ...details, piano: e.target.value })} className="h-12" />
                <Input placeholder="C'è ascensore? Sì / No" value={details.ascensore} onChange={(e) => setDetails({ ...details, ascensore: e.target.value })} className="h-12" />
                <Textarea placeholder="Messaggio libero" value={details.messaggio} onChange={(e) => setDetails({ ...details, messaggio: e.target.value })} rows={4} />
              </div>
            )}

            {current.custom === "form" && (
              <div className="space-y-3">
                <p className="text-sm text-[#5C5C59] mb-2">Ti ricontatteremo per raccogliere le ultime informazioni e valutare insieme il sopralluogo gratuito.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input placeholder="Nome *" value={lead.first_name} onChange={(e) => setLead({ ...lead, first_name: e.target.value })} className="h-12" data-testid="sop-first-name" />
                  <Input placeholder="Cognome *" value={lead.last_name} onChange={(e) => setLead({ ...lead, last_name: e.target.value })} className="h-12" data-testid="sop-last-name" />
                </div>
                <Input placeholder="Telefono *" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="h-12" data-testid="sop-phone" />
                <Input type="email" placeholder="Email *" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="h-12" data-testid="sop-email" />
                <label className="flex items-start gap-3 text-sm text-[#5C5C59]">
                  <Checkbox checked={lead.privacy} onCheckedChange={(v) => setLead({ ...lead, privacy: !!v })} data-testid="sop-privacy" />
                  <span>Ho letto l'<Link to="/privacy" className="underline">informativa privacy</Link>. Chiedo di essere ricontattato in merito alla mia richiesta.</span>
                </label>
                <button onClick={submit} disabled={submitting} className="btn-primary w-full py-4 rounded-full font-medium disabled:opacity-60" data-testid="sop-submit">
                  {submitting ? "Invio..." : "Richiedi sopralluogo gratuito"}
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {current.custom !== "form" && (
          <div className="fixed bottom-0 left-0 right-0 bg-warm-white/90 backdrop-blur-md border-t border-brand p-4">
            <div className="max-w-2xl mx-auto flex justify-between gap-3">
              <button onClick={prev} disabled={step === 0} className="px-5 py-3 rounded-full text-sm border border-brand disabled:opacity-40 flex items-center gap-2">
                <ArrowLeft size={16} /> Indietro
              </button>
              <button onClick={next} disabled={!canNext} className="btn-primary px-7 py-3 rounded-full text-sm flex items-center gap-2 disabled:opacity-40" data-testid="sop-next">
                Continua <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      <WhatsAppFloat number={siteSettings.whatsapp_number} />
    </div>
  );
}
